import { useStore } from '@nanostores/react';
import { motion } from 'framer-motion';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { LargeProjectError } from './LargeProjectError';
import { SupabaseStopgapButton } from './supabase/SupabaseStopgapButton';
import { AssistantActionButton, AssistantActionButtons } from '~/components/chat/AssistantActionButtons';
import { AssistantMessage } from '~/components/chat/AssistantMessage';
import { DiscordSupportModal } from '~/components/chat/DiscordSupportModal';
import { Markdown } from '~/components/chat/Markdown';
import { UserMessage } from '~/components/chat/UserMessage';
import { PotentialProblems } from '~/components/chat/problems/PotentialProblems';
import { SupabaseMigration } from '~/components/chat/supabase/SupabaseMigration';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { MessageGradient } from '~/components/ui/MessageGradient';
import { OutOfTokensAlert } from '~/components/ui/pricing/OutOfTokensAlert';
import { MessageProvider } from '~/lib/context/message';
import { countErrorRepeats } from '~/lib/error-handling';
import { chatStore } from '~/lib/stores/chat';
import type { ChatMessage, Message } from '~/types/chat';
import type { UserData } from '~/types/user';
import { classNames } from '~/utils/classNames';
import { renderLogger } from '~/utils/logger';
import { isThinkingAnnotation } from '~/utils/thinking';

interface MessagesProps {
  id?: string;
  className?: string;
  isStreaming?: boolean;
  messages?: Message[];
  messageBoxHeight?: number;
  isAtBottom?: boolean;
  user?: UserData;
  sendMessage?: (messageInput?: string) => void;
  scrollToBottom: ScrollToBottom;
  onRollback?: (message: Message) => void;
  resubmit?: (message: ChatMessage) => void;
  acceptSupabaseChange: (changeId: string) => Promise<void>;
  discardSupabaseChange: (changeId: string, removeMessage?: boolean) => Promise<void>;
}

export const Messages = memo(
  React.forwardRef<HTMLDivElement, MessagesProps>((props: MessagesProps, ref) => {
    renderLogger.trace('Messages');

    const {
      id,
      isStreaming = false,
      isAtBottom = true,
      messages = [],
      className,
      messageBoxHeight = 0,
      sendMessage,
      onRollback,
      scrollToBottom,
      resubmit,
      acceptSupabaseChange,
      discardSupabaseChange,
    } = props;

    const [pinnedMessageHeight, setPinnedMessageHeight] = useState(0);
    const [finished, setFinished] = useState(false);
    const [isLoading, setIsLoading] = useState(messages.length === 0);

    const isLoadingPreviousMessages = useStore(chatStore.isLoadingPreviousMessages);

    const visibleMessages = useMemo(() => messages.filter(({ hidden }) => !hidden), [messages]);

    const hasPinnedMessage = pinnedMessageHeight > 0;

    const bottom = messageBoxHeight + pinnedMessageHeight;

    useEffect(() => {
      if (!isStreaming && !isAtBottom) {
        setFinished(true);
      }
    }, [isStreaming]);

    useEffect(() => {
      if (isAtBottom) {
        // timeout to allow gradual fade out
        setTimeout(() => setFinished(false), 150);
      }
    }, [isAtBottom]);

    useEffect(() => {
      if (visibleMessages.length > 0) {
        setIsLoading(false);
      }
    }, [visibleMessages]);

    const [isDiscordModalOpen, setIsDiscordModalOpen] = useState(false);

    const reportIssue = useCallback(() => {
      setIsDiscordModalOpen(true);
    }, []);

    const lastChatMessage = visibleMessages.findLast((message) => message.type === 'chat');
    const latestIsProblem = visibleMessages.at(-1)?.type === 'problems';

    const consecutiveProblemCount = latestIsProblem ? countErrorRepeats(visibleMessages)[0] : 0;

    return (
      <>
        <DiscordSupportModal open={isDiscordModalOpen} onClose={() => setIsDiscordModalOpen(false)} />

        <section
          data-testid="chat"
          id={id}
          aria-label="Chat"
          ref={ref}
          className={className}
          translate={isStreaming ? 'no' : undefined}
        >
          {isLoading && visibleMessages.length === 0 && (
            <div className="w-full text-bolt-elements-textSecondary i-svg-spinners:3-dots-fade size-6" />
          )}

          {isLoadingPreviousMessages && (
            <div className="w-full h-6 mt-4 flex justify-center items-center">
              <LoadingDots text="Loading previous messages" />
            </div>
          )}

          {visibleMessages.map((message, index) => {
            const { role } = message;
            const isUserMessage = role === 'user';
            const isLast = index === visibleMessages.length - 1;

            const attachments = message.experimental_attachments?.map((attachment) => {
              return {
                url: `${location.origin}/api/upload/${encodeURIComponent(attachment.url)}`,
                name: attachment.name,
                contentType: attachment.contentType,
              };
            });

            const isLargeProjectError = message.error?.code === 'project-too-big';

            const shouldDisplayOutOfTokensAlert = message.error?.code === 'rate-limited';

            const shouldShowError =
              !isLargeProjectError &&
              !shouldDisplayOutOfTokensAlert &&
              (isUserMessage ? message.status === 'unsent' : message.status === 'incomplete');

            const isRetryable =
              !isStreaming && (message.error?.isRetryable ?? shouldShowError) && message.id === lastChatMessage?.id;

            if (message.type === 'problems') {
              return (
                <PotentialProblems
                  key={message.id}
                  isLast={isLast}
                  consecutiveProblemCount={consecutiveProblemCount}
                  resolved={message.resolved}
                  problems={message.problems}
                  onStickyHeight={setPinnedMessageHeight}
                  scrollToBottom={scrollToBottom}
                  messageBoxHeight={messageBoxHeight}
                />
              );
            }

            if (message.type === 'supabase') {
              const { change, changeStatus } = message;

              switch (change.type) {
                case 'migration': {
                  return (
                    <SupabaseMigration
                      key={message.id}
                      isLast={isLast}
                      messageBoxHeight={messageBoxHeight}
                      change={change}
                      changeStatus={changeStatus}
                      onStickyHeight={setPinnedMessageHeight}
                      scrollToBottom={scrollToBottom}
                      acceptSupabaseChange={acceptSupabaseChange}
                    />
                  );
                }
                case 'connect-required': {
                  return (
                    <SupabaseStopgapButton
                      key={message.id}
                      isLast={isLast}
                      messageBoxHeight={messageBoxHeight}
                      change={change}
                      onStickyHeight={setPinnedMessageHeight}
                      scrollToBottom={scrollToBottom}
                      discardSupabaseChange={discardSupabaseChange}
                    />
                  );
                }
                case 'migration-error':
                default: {
                  return (
                    <PotentialProblems
                      key={message.id}
                      isLast={isLast}
                      consecutiveProblemCount={change.problems.length}
                      messageBoxHeight={messageBoxHeight}
                      resolved={message.changeStatus !== 'pending'}
                      problems={change.problems}
                      onStickyHeight={setPinnedMessageHeight}
                      scrollToBottom={scrollToBottom}
                      customResolve={() => acceptSupabaseChange(change.id)}
                      customClear={() => discardSupabaseChange(change.id, true)}
                    />
                  );
                }
              }
            }

            const containsThinking =
              message.role === 'assistant' &&
              (message.parts.some((part) => part.type === 'reasoning' && part.reasoning) ||
                message.annotations?.some((annotation) => isThinkingAnnotation(annotation)));

            const shouldRenderMessage = message.content || containsThinking || shouldShowError;

            if (!shouldRenderMessage) {
              return null;
            }

            const disableQuickActions =
              isStreaming ||
              (!isLast && (index < visibleMessages.length - 2 || visibleMessages.at(-1)?.type !== 'problems'));

            return (
              <Fragment key={message.id}>
                <div
                  className={classNames('relative flex flex-col overflow-hidden mx-[var(--chat-padding)]', {
                    'bg-bolt-elements-messages-background px-4 py-3 rounded-md self-end': isUserMessage,
                  })}
                  data-message-id={message.id}
                >
                  <div className="grid grid-col-1 w-full">
                    <MessageProvider value={message}>
                      {isUserMessage ? (
                        <UserMessage
                          renderHtml={message.renderHtml}
                          attachments={attachments}
                          inspectedElement={message.inspectedElement}
                          fileSelections={message.fileSelections}
                          content={message.input ?? message.content}
                        />
                      ) : (
                        <AssistantMessage
                          sendMessage={sendMessage}
                          content={message.content}
                          annotations={message.annotations}
                          disableQuickActions={disableQuickActions}
                          parts={message.parts}
                          scrollToBottom={scrollToBottom}
                          actionButtons={
                            <AssistantActionButtons>
                              <AssistantActionButton disabled={isStreaming} onClick={reportIssue}>
                                <div className="i-ph:warning text-lg" />
                                Report issue
                              </AssistantActionButton>
                            </AssistantActionButtons>
                          }
                        />
                      )}
                    </MessageProvider>
                  </div>

                  {shouldShowError && (
                    <div className="pt-4">
                      <Alert
                        title={message.error?.title ?? 'Error'}
                        type="warning"
                        direction="row"
                        actions={
                          <>
                            {isRetryable && resubmit && (
                              <Button variant="tint" size="sm" color="warning" onClick={() => resubmit(message)}>
                                Retry
                              </Button>
                            )}
                          </>
                        }
                      >
                        <Markdown className="text-bolt-ds-textSecondary" html>
                          {message.error?.message ??
                            (message.status === 'unsent'
                              ? 'This message was not sent.'
                              : `This message didn't complete its response.`)}
                        </Markdown>
                      </Alert>
                    </div>
                  )}
                </div>

                {shouldDisplayOutOfTokensAlert && <OutOfTokensAlert className="mx-4" />}

                {isLargeProjectError && (
                  <LargeProjectError
                    className="mx-[var(--chat-padding)]"
                    isLast={isLast}
                    messageId={message.messageId || message.id}
                    onUndo={() => onRollback?.(message)}
                  />
                )}
              </Fragment>
            );
          })}

          {!hasPinnedMessage && !isLoading && visibleMessages.length > 0 && (
            <MessageGradient
              className="sticky flex flex-col justify-center w-full -mb-6"
              style={{ bottom: messageBoxHeight }}
            />
          )}
        </section>

        {scrollToBottom && (
          <motion.div
            className="sticky h-0 mx-auto z-scroller"
            initial={false}
            style={{
              bottom: !hasPinnedMessage ? 38 + bottom + (isStreaming ? 24 : 12) : bottom + (isStreaming ? 24 : 12),
            }}
            transition={{ duration: 0.1 }}
            animate={{
              opacity: isAtBottom ? 0 : 1,
              visibility: isAtBottom ? 'hidden' : 'initial',
              translateY: isAtBottom ? 32 : 0,
            }}
          >
            <button
              className={classNames(
                'flex items-center justify-center size-8 rounded-full shadow shadow-bolt-elements-messages-scroller-shadow',
                {
                  'bg-bolt-elements-messages-scroller-background text-bolt-elements-messages-scroller-icon': !finished,
                  'bg-bolt-elements-messages-scroller-finishedBackground text-bolt-elements-messages-scroller-finishedIcon':
                    finished,
                },
              )}
              onClick={() => scrollToBottom({ animation: { damping: 1, mass: 1.1 } })}
            >
              <div className="i-ph:arrow-down size-4.5" />
            </button>
          </motion.div>
        )}
      </>
    );
  }),
);
